// Central localStorage quota management. All sizeable localStorage writes go
// through safeSetItem(): when the origin quota is full it frees space by
// evicting the most disposable data first, then retries — instead of every
// writer silently giving up (which made drafts, run progress and copy-paste
// all stop persisting once the quota filled).
//
// Eviction order (safest first):
//   1. The execution trace — debug data regenerated on every run.
//   2. Run-progress records for OTHER workflows, oldest first.
//   3. Legacy clean (non-dirty) flow caches, oldest first. Flow drafts now
//      live in IndexedDB (localFlowCache), so these only exist on machines
//      that haven't migrated yet. Dirty drafts are NEVER evicted.

var TRACE_KEY = 'alleflow_execution_trace';
var PROGRESS_PREFIX = 'alleflow_wf_progress_';
var FLOW_PREFIX = 'aflow:flow:';
var FLOW_DIRTY_INDEX_KEY = 'aflow:flow:dirty';

var DEFAULT_PROGRESS_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function hasStorage() {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch (e) {
    return false;
  }
}

function isQuotaError(err) {
  if (!err) return false;
  return err.name === 'QuotaExceededError'
    || err.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || err.code === 22
    || err.code === 1014;
}

// Collects localStorage keys with the given prefix, each with the updatedAt
// parsed from its JSON value (0 when unreadable), sorted oldest first.
function listByPrefixOldestFirst(prefix, filter) {
  var found = [];
  for (var i = 0; i < window.localStorage.length; i += 1) {
    var key = window.localStorage.key(i);
    if (!key || key.indexOf(prefix) !== 0) continue;
    var entry = null;
    try { entry = JSON.parse(window.localStorage.getItem(key)); } catch (e) {}
    if (filter && !filter(key, entry)) continue;
    found.push({ key: key, updatedAt: (entry && entry.updatedAt) || 0 });
  }
  found.sort(function(a, b) { return a.updatedAt - b.updatedAt; });
  return found;
}

function removeKeys(items) {
  items.forEach(function(item) {
    try { window.localStorage.removeItem(item.key); } catch (e) {}
  });
  return items.length;
}

// Each eviction stage frees one category of data and returns how many keys it
// removed; safeSetItem retries the write after every stage that freed space.
var EVICTION_STAGES = [
  function dropExecutionTrace(protectedKey) {
    if (protectedKey === TRACE_KEY) return 0;
    return removeKeys([{ key: TRACE_KEY }]);
  },
  function dropOtherProgressRecords(protectedKey) {
    return removeKeys(listByPrefixOldestFirst(PROGRESS_PREFIX, function(key) {
      return key !== protectedKey;
    }));
  },
  function dropCleanLegacyFlowCaches(protectedKey) {
    return removeKeys(listByPrefixOldestFirst(FLOW_PREFIX, function(key, entry) {
      if (key === protectedKey || key === FLOW_DIRTY_INDEX_KEY) return false;
      return !(entry && entry.dirty); // never evict an unsaved draft
    }));
  }
];

// Writes key=value, evicting disposable data and retrying on quota errors.
// Returns true when the value was stored.
function safeSetItem(key, value) {
  if (!hasStorage()) return false;
  for (var stage = -1; stage < EVICTION_STAGES.length; stage += 1) {
    if (stage >= 0) {
      var freed = 0;
      try { freed = EVICTION_STAGES[stage](key); } catch (e) {}
      if (!freed) continue; // nothing changed — retrying would fail identically
      console.log('[storageQuota] Freed ' + freed + ' key(s) via stage ' + (stage + 1) + ' to store "' + key + '"');
    }
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (err) {
      if (!isQuotaError(err)) return false;
    }
  }
  console.log('[storageQuota] Still over quota after eviction — could not store "' + key + '"');
  return false;
}

function safeRemoveItem(key) {
  if (!hasStorage()) return;
  try { window.localStorage.removeItem(key); } catch (e) {}
}

// Startup sweep: run-progress records only matter shortly after the run they
// belong to (resume-from-node). Anything older than maxAgeMs is dead weight.
function sweepStaleProgress(maxAgeMs) {
  if (!hasStorage()) return 0;
  var cutoff = Date.now() - (maxAgeMs || DEFAULT_PROGRESS_MAX_AGE_MS);
  var stale = listByPrefixOldestFirst(PROGRESS_PREFIX, function(key, entry) {
    return !entry || !entry.updatedAt || entry.updatedAt < cutoff;
  });
  var removed = removeKeys(stale);
  if (removed) console.log('[storageQuota] Swept ' + removed + ' stale run-progress record(s)');
  return removed;
}

module.exports = {
  safeSetItem: safeSetItem,
  safeRemoveItem: safeRemoveItem,
  sweepStaleProgress: sweepStaleProgress,
  isQuotaError: isQuotaError
};
